import { ButtonLink } from "@components/ui/Button";
import { PageInfo } from "@graphql/types";
import { Trans } from "@helpers/translate";
import { useRouter } from "next/router";
import React, { FC } from "react";
import { Pagination as AntdPagination } from "antd/lib";

interface PaginationProps {
  pageInfo: PageInfo;
  offsetPagination?: boolean;
  showPageOne?: boolean;
  showIndexBasedPagination?: boolean;
  totalCount?: number;
  sizePerPage?: number;
}

const Pagination: FC<PaginationProps> = ({
  pageInfo,
  offsetPagination = false,
  showPageOne = true,
  showIndexBasedPagination = false,
  sizePerPage = 24,
}) => {
  const router = useRouter();
  const { asPath } = useRouter();

  const searchParams = new URLSearchParams(
    asPath.includes("?") ? asPath : `${asPath}?q=`
  );
  let prevPageUrl = "";
  let nextPageUrl = "";

  if (pageInfo.hasPreviousPage) {
    searchParams.delete("after");
    searchParams.delete("before");
    searchParams.append("before", pageInfo.startCursor);
    prevPageUrl = decodeURIComponent(searchParams.toString());
  }

  if (pageInfo.hasNextPage) {
    searchParams.delete("after");
    searchParams.delete("before");
    searchParams.append("after", pageInfo.endCursor);
    nextPageUrl = decodeURIComponent(searchParams.toString());
  }

  if (offsetPagination) {
    searchParams.delete("after");
    searchParams.delete("before");

    const page = pageInfo.page || 1;
    if (pageInfo.hasNextPage) {
      searchParams.delete("page");
      searchParams.append("page", String(page + 1));
      nextPageUrl = decodeURIComponent(searchParams.toString());
    }

    if (pageInfo.hasPreviousPage) {
      searchParams.delete("page");

      if (showPageOne) {
        searchParams.append("page", String(page - 1));
      } else {
        if (page - 1 > 1) {
          searchParams.append("page", String(page - 1));
        }
      }

      prevPageUrl = decodeURIComponent(searchParams.toString());
    }
  }

  if (showIndexBasedPagination) {
    const page = Number(pageInfo.page || "1");
    const totalPages = Number(pageInfo.totalPages || "1");

    const itemRender = (
      page: number,
      type: "page" | "prev" | "next" | "jump-prev" | "jump-next",
      element: React.ReactNode
    ) => {
      searchParams.delete("page");

      if (type === "next") {
        return element;
      }

      if (type === "prev") {
        return element;
      }

      if (type === "jump-prev") {
        return (
          <div className="ant-pagination-item-link">
            <div className="ant-pagination-item-container">
              <span
                role="img"
                aria-label="double-left"
                className="anticon anticon-double-left ant-pagination-item-link-icon"
              >
                <svg
                  viewBox="64 64 896 896"
                  focusable="false"
                  data-icon="double-left"
                  width="1em"
                  height="1em"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path d="M272.9 512l265.4-339.1c4.1-5.2.4-12.9-6.3-12.9h-77.3c-4.9 0-9.6 2.3-12.6 6.1L186.8 492.3a31.99 31.99 0 000 39.5l255.3 326.1c3 3.9 7.7 6.1 12.6 6.1H532c6.7 0 10.4-7.7 6.3-12.9L272.9 512zm304 0l265.4-339.1c4.1-5.2.4-12.9-6.3-12.9h-77.3c-4.9 0-9.6 2.3-12.6 6.1L490.8 492.3a31.99 31.99 0 000 39.5l255.3 326.1c3 3.9 7.7 6.1 12.6 6.1H836c6.7 0 10.4-7.7 6.3-12.9L576.9 512z"></path>
                </svg>
              </span>
              <span className="ant-pagination-item-ellipsis">•••</span>
            </div>
          </div>
        );
      }

      if (type === "jump-next") {
        return (
          <div className="ant-pagination-item-link">
            <div className="ant-pagination-item-container">
              <span
                role="img"
                aria-label="double-right"
                className="anticon anticon-double-right ant-pagination-item-link-icon"
              >
                <svg
                  viewBox="64 64 896 896"
                  focusable="false"
                  data-icon="double-right"
                  width="1em"
                  height="1em"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path d="M533.2 492.3L277.9 166.1c-3-3.9-7.7-6.1-12.6-6.1H188c-6.7 0-10.4 7.7-6.3 12.9L447.1 512 181.7 851.1A7.98 7.98 0 00188 864h77.3c4.9 0 9.6-2.3 12.6-6.1l255.3-326.1c9.1-11.7 9.1-27.9 0-39.5zm304 0L581.9 166.1c-3-3.9-7.7-6.1-12.6-6.1H492c-6.7 0-10.4 7.7-6.3 12.9L751.1 512 485.7 851.1A7.98 7.98 0 00492 864h77.3c4.9 0 9.6-2.3 12.6-6.1l255.3-326.1c9.1-11.7 9.1-27.9 0-39.5z"></path>
                </svg>
              </span>
              <span className="ant-pagination-item-ellipsis">•••</span>
            </div>
          </div>
        );
      }

      if (page > 1) {
        searchParams.append("page", String(page));
      }

      nextPageUrl = decodeURIComponent(searchParams.toString());

      // FOR SEO, handled page numbers alone with a tag.
      if (type === "page") {
        return <a href={nextPageUrl}>{page}</a>;
      }

      return (
        <a className="ant-pagination-item-link" href={nextPageUrl}>
          {element}
        </a>
      );
    };

    return (
      <AntdPagination
        itemRender={itemRender}
        defaultCurrent={page}
        current={page}
        showSizeChanger={false}
        pageSize={Number(sizePerPage)}
        total={Number(totalPages * sizePerPage)}
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        onChange={(page, _) => {
          searchParams.delete("page");
          searchParams.append("page", String(page));
          const pageUrl = decodeURIComponent(searchParams.toString());
          router.push(pageUrl);
        }}
      />
    );
  }

  return (
    <div className="flex items-center justify-center">
      {prevPageUrl && (
        <ButtonLink
          variant="default"
          className="w-28 mr-4"
          href={prevPageUrl}
          title="Gowithguide - Prev"
        >
          <Trans id="pagination.button.prev">Prev</Trans>
        </ButtonLink>
      )}
      {nextPageUrl && (
        <ButtonLink
          variant="default"
          className="w-28"
          href={nextPageUrl}
          title="Gowithguide - Next"
        >
          <Trans id="pagination.button.next">Next</Trans>
        </ButtonLink>
      )}
    </div>
  );
};

export default Pagination;
